import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, EyeOff, Lock, UserX, UploadCloud, Send } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import useLenis from "@/hooks/useLenis";
import api from "@/lib/api";

const STANDARDS = [
  { icon: ShieldCheck, text: "Trauma-informed storytelling and dignity-first editing" },
  { icon: UserX, text: "Anonymous option — an alias is always allowed" },
  { icon: Lock, text: "Consent-first publishing and controlled release" },
  { icon: EyeOff, text: "No doxxing or identifying private information" },
];

export default function SharePage() {
  useLenis();
  const [form, setForm] = useState({ alias: "", contact: "", format: "written", story: "" });
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please confirm consent to submit your story."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("alias", form.alias || "Anonymous");
      fd.append("contact", form.contact);
      fd.append("format", form.format);
      fd.append("story", form.story);
      fd.append("consent", "true");
      if (file) fd.append("file", file);
      const { data } = await api.post("/stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Story submitted for review");
      setDone(true);
      setForm({ alias: "", contact: "", format: "written", story: "" });
      setFile(null); setConsent(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return data;
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-imi-bg text-imi-text min-h-screen">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-imi-text/50 hover:text-imi-gold transition-colors mb-10">
            <ArrowLeft size={16} /> Back home
          </Link>

          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.35em] text-imi-gold mb-5">The Story Platform</p>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">Share your <span className="italic gold-gradient-text">story</span>.</h1>
            <p className="mt-8 text-imi-text/60 text-lg leading-relaxed max-w-2xl">
              A secure, survivor-first pathway for formerly and currently incarcerated people to share their stories —
              written, audio, or video — with options for anonymity and consent-first publishing.
            </p>
          </Reveal>

          {done && (
            <Reveal className="mt-10">
              <div data-testid="story-success" className="border border-imi-gold/40 bg-imi-gold/5 p-6 flex items-start gap-4">
                <ShieldCheck className="text-imi-gold shrink-0 mt-1" />
                <div>
                  <div className="font-serif text-xl text-imi-text">Thank you for trusting us.</div>
                  <p className="text-imi-text/60 mt-1">Your story has been submitted for review. It will be published <span className="text-imi-gold">only after it is approved</span> — nothing goes online without consent.</p>
                </div>
              </div>
            </Reveal>
          )}

          <div className="mt-14 grid lg:grid-cols-12 gap-10">
            {/* Standards */}
            <Reveal className="lg:col-span-5">
              <div className="border border-imi-border bg-imi-surface p-8 md:p-10 sticky top-28">
                <h2 className="font-serif text-2xl md:text-3xl text-imi-text mb-8">Submission Standards</h2>
                <ul className="space-y-6">
                  {STANDARDS.map((s, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="w-10 h-10 shrink-0 border border-imi-gold/40 flex items-center justify-center text-imi-gold"><s.icon size={18} /></span>
                      <span className="text-imi-text/70 leading-relaxed pt-1.5">{s.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 pt-6 border-t border-imi-border text-sm text-imi-text/50 leading-relaxed">
                  Every submission is <span className="text-imi-gold">reviewed by our team</span> before it is ever made public. You stay in control of your name and your truth.
                </div>
              </div>
            </Reveal>

            {/* Form */}
            <Reveal delay={0.1} className="lg:col-span-7">
              <form onSubmit={submit} data-testid="story-form" className="space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Name / Alias *</label>
                    <input required value={form.alias} onChange={set("alias")} data-testid="story-alias" placeholder="Your name or alias"
                      className="w-full bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/30 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Contact (email/phone) *</label>
                    <input required value={form.contact} onChange={set("contact")} data-testid="story-contact" placeholder="So IMI can follow up"
                      className="w-full bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/30 transition-colors" />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Format *</label>
                  <select value={form.format} onChange={set("format")} data-testid="story-format"
                    className="w-full bg-imi-surface border border-imi-border focus:border-imi-gold outline-none py-3 px-3 text-imi-text transition-colors">
                    <option value="written">Written</option>
                    <option value="audio">Audio</option>
                    <option value="video">Video</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Your Story *</label>
                  <textarea required rows={7} value={form.story} onChange={set("story")} data-testid="story-text"
                    placeholder="Share what happened, what you witnessed, or what you survived…"
                    className="w-full bg-transparent border-b border-imi-border focus:border-imi-gold outline-none py-3 text-imi-text placeholder:text-imi-text/30 resize-none transition-colors" />
                </div>

                {(form.format === "audio" || form.format === "video") && (
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.2em] text-imi-text/50 mb-2">Attach {form.format} (optional)</label>
                    <label className="cursor-pointer border border-dashed border-imi-text/30 hover:border-imi-gold bg-imi-surface p-5 flex items-center gap-4 transition-colors" data-testid="story-file-label">
                      <UploadCloud className="text-imi-gold" />
                      <span className="text-sm text-imi-text/60">{file ? file.name : `Click to upload your ${form.format} file`}</span>
                      <input type="file" accept={form.format === "audio" ? "audio/*" : "video/*"} className="hidden"
                        onChange={(e) => setFile(e.target.files[0])} data-testid="story-file" />
                    </label>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer" data-testid="story-consent-label">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="story-consent"
                    className="mt-1 accent-[#d4af5a] w-4 h-4" />
                  <span className="text-sm text-imi-text/60 leading-relaxed">
                    I understand my story will be <span className="text-imi-gold">reviewed before publishing</span>, and I consent to IMI contacting me about it. I can request changes or removal at any time.
                  </span>
                </label>

                <button disabled={loading} type="submit" data-testid="story-submit"
                  className="group inline-flex items-center gap-3 bg-imi-gold text-imi-bg px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:bg-imi-goldlight transition-colors disabled:opacity-60">
                  {loading ? "Submitting…" : "Submit for review"} <Send size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
